import { computed, shallowReactive } from 'vue';
import type { VPConfirmationDialogOutcome } from '../components/VPConfirmationDialog.vue';
import type { VPTextInputDialogOutcome } from '../components/VPTextInputDialog.vue';

export type ConfirmationDialogOptions = {
  title: string;
  message?: string;
  confirmLabel: string;
  cancelLabel?: string;
  checkboxLabel?: string;
  action?: (outcome: VPConfirmationDialogOutcome) => unknown;
};

export type TextInputDialogOptions = {
  title: string;
  message?: string;
  label: string;
  initialValue?: string;
  placeholder?: string;
  confirmLabel: string;
  cancelLabel?: string;
  required?: boolean;
  requiredMessage?: string;
};

type ConfirmationDialogRequest = {
  kind: 'confirmation';
  id: number;
  options: ConfirmationDialogOptions;
  resolve: (outcome: VPConfirmationDialogOutcome) => void;
  reject: (error: unknown) => void;
};

type TextInputDialogRequest = {
  kind: 'textInput';
  id: number;
  options: TextInputDialogOptions;
  resolve: (outcome: VPTextInputDialogOutcome) => void;
};

// Module-level so stores and plain helpers can ask too; one shared queue keeps both kinds of dialog one at a time.
const queue = shallowReactive<(ConfirmationDialogRequest | TextInputDialogRequest)[]>([]);
let nextId = 0;

/** Opens the confirmation dialog; resolves with the answer, or rejects with a failed `action`'s error. */
export const openConfirmationDialog = (options: ConfirmationDialogOptions) =>
  new Promise<VPConfirmationDialogOutcome>((resolve, reject) => {
    queue.push({ kind: 'confirmation', id: nextId++, options, resolve, reject });
  });

/** Opens the text-input dialog; resolves with the entered text on confirm, or a cancel. */
export const openTextInputDialog = (options: TextInputDialogOptions) =>
  new Promise<VPTextInputDialogOutcome>(resolve => {
    queue.push({ kind: 'textInput', id: nextId++, options, resolve });
  });

export const useConfirmationDialogHost = () => ({
  current: computed(() => queue[0]),
  settle: (id: number, outcome: VPConfirmationDialogOutcome, failure?: { error: unknown }) => {
    const request = queue[0];

    if (request?.id !== id || request.kind !== 'confirmation') {
      return;
    }

    queue.shift();

    if (failure) {
      request.reject(failure.error);
    } else {
      request.resolve(outcome);
    }
  },
  settleTextInput: (id: number, outcome: VPTextInputDialogOutcome) => {
    const request = queue[0];

    if (request?.id !== id || request.kind !== 'textInput') {
      return;
    }

    queue.shift();
    request.resolve(outcome);
  },
  cancel: (id: number) => {
    const request = queue[0];

    if (request?.id !== id) {
      return;
    }

    queue.shift();

    if (request.kind === 'confirmation') {
      request.resolve({ action: 'cancel', checked: false });
    } else {
      request.resolve({ action: 'cancel' });
    }
  },
});
